import React, { useMemo, useEffect } from 'react';
import { Badge } from '../components/StatusBadge';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ClockIcon, CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { Job } from '../types';
import { TableCard, TableHeader, TableViewport, SortableTh, EmptyRow, tdClass, usePageSize, SortOrder } from '../components/table/TableKit';
import { StatusCard } from '../components/cards/StatCards';
import { formatDuration } from '../utils/dateTime';
import RelativeTime from '../components/RelativeTime';
import CopyIconButton from '../components/CopyIconButton';
import { ErrorNotice } from '../components/ErrorNotice';
import { urlEnum, urlPositiveInt, urlString, useUrlState } from '../hooks/useUrlState';
import { useSearchDraft } from '../hooks/useSearchDraft';
import { usePageClamp } from '../hooks/usePageClamp';
import { fromHere } from '../hooks/useBackTo';
import { formatEstimateCents } from '../components/converter/cliCost';

type StatusFilter = 'all' | 'pending' | 'running' | 'completed' | 'failed';

const URL_SPEC = {
  status: urlEnum<StatusFilter>(['all', 'pending', 'running', 'completed', 'failed'], 'all'),
  q: urlString(),
  sort: urlEnum(['project_name', 'status', 'progress', 'duration', 'supported_tokens', 'estimated_conversion_cost', 'created_at'] as const, 'created_at'),
  order: urlEnum(['asc', 'desc'] as const, 'desc'),
  page: urlPositiveInt(),
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pageSize, setPageSize] = usePageSize('analyzer-jobs');
  const [state, setUrlState] = useUrlState(URL_SPEC);
  const [searchQuery, setSearchQuery] = useSearchDraft(state.q, (q) => setUrlState({ q, page: 1 }));
  const onSort = (sort: string, order: SortOrder) =>
    setUrlState({ sort: sort as typeof state.sort, order, page: 1 });
  const instanceInfo = apiService.getInstanceInfo();
  // The cost estimate is for saved instances only. The API omits it for temp instances.
  const showCost = instanceInfo?.instanceMode === 'persistent';
  const columnCount = showCost ? 7 : 6;

  const { data: health, isError: healthError } = useQuery({
    queryKey: ['health'],
    queryFn: apiService.getHealth,
    // 15s keeps several open tabs inside the public per-IP rate limit.
    refetchInterval: 15000,
  });

  // Log health status changes
  useEffect(() => {
    if (health) {
      console.debug('API Health Check:', {
        status: health.status,
        database: health.database,
        version: health.version
      });
    }
    if (healthError) {
      console.error('API Health Check Failed:', healthError);
    }
  }, [health, healthError]);

  // Server-side pagination: status filter and search are query params
  const { data: jobsData, isLoading, isPlaceholderData, error: jobsError } = useQuery({
    queryKey: ['jobs', state.page, pageSize, state.status, state.q, state.sort, state.order],
    queryFn: () => apiService.getJobs(
      state.page,
      state.status !== 'all' ? state.status : undefined,
      { limit: pageSize, search: state.q || undefined, sort: state.sort, order: state.order }
    ),
    placeholderData: (previousData) => previousData,
    // Poll only while a job is active. An idle list can still go stale, so
    // refresh it when the user comes back to the tab.
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const data = query.state.data as { status_counts?: Record<string, number> } | undefined;
      if (!data) return 10000;
      const counts = data.status_counts ?? {};
      const hasActive = (counts.pending || 0) > 0 || (counts.running || 0) > 0;
      return hasActive ? 10000 : false;
    },
  });

  usePageClamp({
    page: state.page,
    pageSize,
    total: jobsData?.total,
    ready: !isLoading && !isPlaceholderData && !!jobsData,
    setPage: (page) => setUrlState({ page }),
  });

  // Global per-status counts come from the server, independent of filters
  const stats = useMemo(() => {
    return jobsData?.status_counts ?? { pending: 0, running: 0, completed: 0, failed: 0 };
  }, [jobsData]);


  const getElapsedTime = (job: Job) => {
    return formatDuration(job.started_at, job.completed_at);
  };


  return (
    <div>
      {jobsError && <ErrorNotice message="Failed to load jobs." />}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-baseline gap-3">
          <h2 className="text-2xl font-bold">Dashboard</h2>
          {instanceInfo && (
            <div className="flex items-center gap-2">
              <span className="text-secondary text-sm">{instanceInfo.instanceKey}</span>
              <CopyIconButton text={instanceInfo.instanceKey} label="instance key" />
            </div>
          )}
        </div>
        <Link
          to="/jobs/new"
          className="btn-primary inline-block font-medium"
        >
          New Analysis
        </Link>
      </div>
      
      {/* Stat Cards - Clickable Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatusCard
          label="Pending"
          value={stats.pending || 0}
          icon={ClockIcon}
          iconClass="text-secondary"
          active={state.status === 'pending'}
          activeClass="border-primary ring-2 ring-primary/50"
          onClick={() => setUrlState({ status: state.status === 'pending' ? 'all' : 'pending', page: 1 })}
        />
        <StatusCard
          label="Running"
          value={stats.running || 0}
          icon={ArrowPathIcon}
          iconClass="text-warning"
          active={state.status === 'running'}
          activeClass="border-warning ring-2 ring-warning/50"
          onClick={() => setUrlState({ status: state.status === 'running' ? 'all' : 'running', page: 1 })}
        />
        <StatusCard
          label="Completed"
          value={stats.completed || 0}
          icon={CheckCircleIcon}
          iconClass="text-primary"
          active={state.status === 'completed'}
          activeClass="border-primary ring-2 ring-primary/50"
          onClick={() => setUrlState({ status: state.status === 'completed' ? 'all' : 'completed', page: 1 })}
        />
        <StatusCard
          label="Failed"
          value={stats.failed || 0}
          icon={XCircleIcon}
          iconClass="text-red-500"
          active={state.status === 'failed'}
          activeClass="border-red-500 ring-2 ring-red-500/50"
          onClick={() => setUrlState({ status: state.status === 'failed' ? 'all' : 'failed', page: 1 })}
        />
      </div>

      {/* Jobs Table */}
      <TableCard>
        <TableHeader
          title="All Jobs"
          search={{ value: searchQuery, onChange: setSearchQuery }}
          pagination={{
            offset: (state.page - 1) * pageSize,
            limit: pageSize,
            total: jobsData?.total ?? 0,
            onOffsetChange: (offset) => setUrlState({ page: offset / pageSize + 1 }),
            onLimitChange: (size) => {
              setPageSize(size);
              setUrlState({ page: 1 });
            },
          }}
        >
          {state.status !== 'all' && (
            <button
              onClick={() => setUrlState({ status: 'all', page: 1 })}
              className="text-sm text-primary hover:text-primary-hover"
            >
              Clear filter
            </button>
          )}
        </TableHeader>

        <TableViewport>
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <SortableTh
                  label="Project"
                  sortKey="project_name"
                  sort={state.sort}
                  order={state.order}
                  onSort={onSort}
                  firstOrder="asc"
                />
                <SortableTh label="Status" sortKey="status" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Progress" sortKey="progress" sort={state.sort} order={state.order} onSort={onSort} className="hidden sm:table-cell" />
                <SortableTh label="Duration" sortKey="duration" sort={state.sort} order={state.order} onSort={onSort} className="hidden lg:table-cell" />
                <SortableTh label="Supported Tokens" sortKey="supported_tokens" sort={state.sort} order={state.order} onSort={onSort} className="hidden lg:table-cell" />
                {showCost && (
                  <SortableTh label="Est. Conversion Cost" sortKey="estimated_conversion_cost" sort={state.sort} order={state.order} onSort={onSort} className="hidden lg:table-cell" />
                )}
                <SortableTh
                  label="Created"
                  sortKey="created_at"
                  sort={state.sort}
                  order={state.order}
                  onSort={onSort}
                  className="hidden md:table-cell"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {isLoading ? (
                <EmptyRow colSpan={columnCount} text="Loading..." />
              ) : (jobsData?.jobs ?? []).length === 0 ? (
                <EmptyRow colSpan={columnCount} text="No jobs found." />
              ) : (
                (jobsData?.jobs ?? []).map((job) => (
                  <tr
                    key={job.id}
                    className="hover:bg-body-bg transition-colors cursor-pointer"
                    onClick={() => navigate(`/jobs/${job.id}`, { state: fromHere(location) })}
                  >
                    <td className={`${tdClass} text-heading max-w-[320px] truncate`} title={job.project_name || undefined}>
                      {job.project_name || '-'}
                    </td>
                    <td className={tdClass}>
                      <Badge value={job.status} />
                    </td>
                    <td className={`${tdClass} hidden sm:table-cell`}>
                      <div className="flex items-center">
                        <div className="w-20 bg-body-bg rounded-full h-2 mr-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                        <span className="text-sm text-secondary">{job.progress}%</span>
                      </div>
                    </td>
                    <td className={`${tdClass} hidden lg:table-cell`}>{getElapsedTime(job)}</td>
                    <td className={`${tdClass} hidden lg:table-cell`}>{job.supported_tokens?.toLocaleString() ?? '—'}</td>
                    {showCost && (
                      <td className={`${tdClass} hidden lg:table-cell`}>{formatEstimateCents(job.estimated_conversion_cost_cents)}</td>
                    )}
                    <td className={`${tdClass} hidden md:table-cell text-secondary`}>
                      <RelativeTime value={job.created_at} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </TableViewport>
      </TableCard>

    </div>
  );
};

export default Dashboard;
